import React, {useEffect, useState} from 'react';
import BTable from 'react-bootstrap/Table';
import {Cell, usePagination, useSortBy, useTable} from 'react-table';
import {FormattedMessage} from 'react-intl';
import {isEmpty} from 'lodash';
import {api} from '../../api';
import ReactTablePagination from './ReactTablePagination';
import PageLoadingIndicator from '../Layout/PageLoadingIndicator';
import DaysUntilColumn from './Table/Columns/DaysUntilColumn';
import ActionColumn from './Table/Columns/ActionColumn';
import AttachmentColumn from './Table/Columns/AttachmentColumn';
import EventNameColumn from './Table/Columns/EventNameColumn';
import StatusColumn from './Table/Columns/StatusColumn';
import TasksColumn from './Table/Columns/TasksColumn';
import DateColumn from "./Table/Columns/DateColumn";
import RadioMonthFilter, {DATE_FILTER_ALL_DATES} from "./Table/GlobalFilters/RadioMonthFilter";
import moment from "moment";

export const URL_SLUG = 'submissions';

export interface PaperSubmissionListingData {
    id: string;
    actions: {
        callForPapersSlug: string;
        submissionId: string;
    };
    attachments: number;
    callForPapersName: string;
    createdAt: string;
    decision: string;
    editable: string;
    event: {
        id: string;
        name: string;
        date: string;
    }
    personInCharge: string;
    planningPlusStatus: string;
    sessionDate: string;
    speaker: string;
    status: {
        isCompleteSubmission: boolean;
        decision: string;
        planningPlusStatus: string;
    }
    submittedBy: string;
    tasks: number;
    title: string;
    topic: string;
    updatedAt: string;
    valid: string;
}

/**
 * Tabellarisches Listing der zuvor getätigten Einreichungen des aktuell eingeloggten Users
 *
 * @constructor
 */
const PaperSubmissionListing: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<PaperSubmissionListingData[]>([]);
    const [filteredData, setFilteredData] = useState<PaperSubmissionListingData[]>([]);

    useEffect(() => {
        const loadSubmissionForUser = async () => {
            const response = await api.fetchSubmissionsForUser();
            setIsLoading(false);
            return response;
        };

        loadSubmissionForUser().then(res => {
            if (res !== undefined) {
                setData(res.results);
                setFilteredData(res.results);
            }
        });
    }, []);

    // siehe https://github.com/tannerlinsley/react-table/discussions/2664#discussioncomment-335443
    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="myarea.listing.column.header.attachment"/>,
                accessor: 'attachments' as const,
                Cell: (cell: Cell) => AttachmentColumn(cell)
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.submittedAt"/>,
                accessor: 'createdAt' as const,
                Cell: (cell: Cell) => DateColumn(cell, 'createdAt')
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.title"/>,
                accessor: 'title' as const,
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.event"/>,
                accessor: 'event' as const,
                Cell: (cell: Cell) => EventNameColumn(cell)
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.status"/>,
                accessor: 'status' as const,
                Cell: (cell: Cell) => StatusColumn(cell)
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.editableUntil"/>,
                accessor: 'editable' as const,
                Cell: (cell: Cell) => DaysUntilColumn({
                    submissionId: cell.row.values.actions.submissionId,
                    date: cell.row.values.editable
                })
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.tasks"/>,
                accessor: 'tasks' as const,
                Cell: (cell: Cell) => TasksColumn(cell)
            },
            {
                Header: <FormattedMessage id="myarea.listing.column.header.actions"/>,
                accessor: 'actions' as const,
                Cell: (cell: Cell) => ActionColumn({
                    callForPapersSlug: cell.row.values.actions.callForPapersSlug,
                    submissionId: cell.row.values.actions.submissionId
                })
            }
        ],
        []
    );

    /**
     * Filtert das Listing anhand eines übergebenen filterValue und gibt nur die Einträge zurück, die jünger als
     * filterValue Monate sind.
     *
     * @param filterValue - nummerische Angabe der unteren Schranke an Monaten, die die relevante Spalte in der
     *                      Vergangenheit liegen darf
     */
    const filterByDate = (filterValue: number | undefined): void => {
        if (filterValue !== DATE_FILTER_ALL_DATES) {
            // moment-date bilden mit dem ausgewählten Monatsfilter
            const filterDate = moment().subtract(filterValue, 'months');
            setFilteredData(data.filter((datum: any) => {
                const date = moment(datum['createdAt']);
                if (date && filterDate) {
                    return date.isAfter(filterDate);
                }

                // sofern wir kein Datum haben, geben wir die Zeile einfach aus weil lieber sehen als nicht ¯\_(ツ)_/¯
                return true;
            }))
        } else {
            setFilteredData(data);
        }
    };

    // Tabellen-Props und State initialisieren
    const {
        getTableProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize}
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: {pageIndex: 0, pageSize: 5}
        },
        useSortBy,
        usePagination
    );

    return (
        <div className="submission-listing-container">
            <h2>
                <FormattedMessage id="myarea.submissions.listing.title"/>
            </h2>

            <p>
                <FormattedMessage id="myarea.submissions.listing.subtitle"/>
            </p>

            {isLoading && <PageLoadingIndicator/>}

            {!isEmpty(data) &&
            <>
                <RadioMonthFilter filterByDate={filterByDate}/>

                <BTable bordered hover size="sm" {...getTableProps()}>
                    <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted
                                            ? column.isSortedDesc
                                                ? <i className="fa fa-sort-down"/>
                                                : <i className="fa fa-sort-up"/>
                                            : <i className="fa fa-sort"/>}
                                        </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => (
                                    <td {...cell.getCellProps()}>
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        )
                    })}
                    </tbody>
                </BTable>
                <ReactTablePagination
                    page={page}
                    pageCount={pageCount}
                    pageOptions={pageOptions}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    gotoPage={gotoPage}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                />
            </>
            }
        </div>
    );
};

export default PaperSubmissionListing;
